import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { testIds } from "@/constants/testIds";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, X, Calendar as CalIcon, Wrench } from "lucide-react";

export default function BlocksView() {
  const [blocks, setBlocks] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    item_id: "",
    quantity: 1,
    start_date: "",
    end_date: "",
    reason: "",
  });

  const load = async () => {
    setLoading(true);
    const [b, i] = await Promise.all([
      api.get("/blocks"),
      api.get("/items"),
    ]);
    setBlocks(b.data);
    setItems(i.data);
    if (!form.item_id && i.data.length > 0)
      setForm((f) => ({ ...f, item_id: i.data[0].id }));
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.item_id || !form.start_date || !form.end_date)
      return toast.error("Compila tutti i campi obbligatori");
    try {
      await api.post("/blocks", {
        item_id: form.item_id,
        quantity: Number(form.quantity),
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason,
      });
      toast.success("Blocco creato");
      setShowForm(false);
      setForm({
        item_id: items[0]?.id || "",
        quantity: 1,
        start_date: "",
        end_date: "",
        reason: "",
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione blocco");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Rimuovere questo blocco?")) return;
    await api.delete(`/blocks/${id}`);
    toast.success("Blocco rimosso");
    load();
  };

  const itemById = (id) => items.find((i) => i.id === id);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-lg sm:text-xl font-black uppercase tracking-tight text-white">
            Blocchi manuali
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Blocca articoli per manutenzione, prestito privato o uso interno — la disponibilità nel catalogo terrà conto di questi blocchi.
          </p>
        </div>
        <button
          data-testid={testIds.adminAddBlockBtn}
          onClick={() => setShowForm(true)}
          className="shrink-0 min-h-[44px] px-4 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-heading font-black uppercase tracking-wider text-xs shadow-lg shadow-rose-900/40 transition flex items-center gap-2"
        >
          <Plus size={14} /> Nuovo blocco
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-zinc-500">
          <Loader2 className="animate-spin mr-2" size={18} /> Caricamento...
        </div>
      ) : blocks.length === 0 ? (
        <div className="text-center py-10 text-zinc-500 text-xs font-mono uppercase tracking-widest border border-dashed border-zinc-800 rounded-xl">
          Nessun blocco attivo
        </div>
      ) : (
        <div className="space-y-2">
          {blocks.map((b) => {
            const it = itemById(b.item_id);
            return (
              <div
                key={b.id}
                data-testid={testIds.adminBlockCard(b.id)}
                className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-3 sm:p-4 flex items-start gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-rose-600/15 border border-rose-500/30 flex items-center justify-center text-rose-400 shrink-0">
                  <Wrench size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-white text-sm break-words">
                    {it ? it.name : "Articolo eliminato"}
                    <span className="text-rose-400 font-mono ml-2">
                      × {b.quantity}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-zinc-500 font-mono mt-0.5 flex-wrap">
                    <CalIcon size={11} />
                    {b.start_date}
                    {b.end_date && b.end_date !== b.start_date && (
                      <>→ {b.end_date}</>
                    )}
                  </div>
                  {b.reason && (
                    <div className="text-xs text-zinc-400 mt-1 break-words">
                      {b.reason}
                    </div>
                  )}
                </div>
                <button
                  data-testid={testIds.adminBlockRemove(b.id)}
                  onClick={() => remove(b.id)}
                  className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-800 hover:bg-rose-950 text-zinc-400 hover:text-rose-400 transition"
                  aria-label="Rimuovi blocco"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowForm(false)}
        >
          <form
            data-testid={testIds.adminBlockForm}
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-lg bg-zinc-900 border border-zinc-800 rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-black uppercase text-white">
                Nuovo blocco
              </h2>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Articolo
                </label>
                <select
                  data-testid={testIds.adminBlockItem}
                  value={form.item_id}
                  onChange={(e) =>
                    setForm({ ...form, item_id: e.target.value })
                  }
                  className="w-full min-h-[44px] px-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
                >
                  {items.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name} (max {i.quantity})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Quantità da bloccare
                </label>
                <input
                  data-testid={testIds.adminBlockQty}
                  type="number"
                  min={1}
                  value={form.quantity}
                  onChange={(e) =>
                    setForm({ ...form, quantity: e.target.value })
                  }
                  className="w-full min-h-[44px] px-3.5 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                    Da
                  </label>
                  <input
                    data-testid={testIds.adminBlockStart}
                    type="date"
                    required
                    value={form.start_date}
                    onChange={(e) =>
                      setForm({ ...form, start_date: e.target.value })
                    }
                    className="w-full min-h-[44px] px-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                    A
                  </label>
                  <input
                    data-testid={testIds.adminBlockEnd}
                    type="date"
                    required
                    min={form.start_date || undefined}
                    value={form.end_date}
                    onChange={(e) =>
                      setForm({ ...form, end_date: e.target.value })
                    }
                    className="w-full min-h-[44px] px-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Motivo (es. manutenzione, prestito privato)
                </label>
                <input
                  data-testid={testIds.adminBlockReason}
                  value={form.reason}
                  onChange={(e) =>
                    setForm({ ...form, reason: e.target.value })
                  }
                  placeholder="Facoltativo"
                  className="w-full min-h-[44px] px-3.5 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm placeholder-zinc-600"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 min-h-[48px] rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-semibold text-xs uppercase tracking-wider transition"
              >
                Annulla
              </button>
              <button
                data-testid={testIds.adminBlockSave}
                type="submit"
                className="flex-1 min-h-[48px] rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-heading font-black uppercase tracking-wider text-xs shadow-lg shadow-rose-900/40 transition"
              >
                Blocca
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
